import { inject, Injectable, Injector } from '@angular/core';
import { User } from '../models/user.model';
import { Router } from '@angular/router';
import { FirebaseErrors } from '../helpers/firebaseErrors.helper';
import { RolesEnum } from '../enums/roles.enum';
import {
  Auth,
  EmailAuthProvider,
  linkWithCredential,
  onIdTokenChanged,
  signInAnonymously,
  signInWithEmailAndPassword,
  signOut,
  UserCredential
} from '@angular/fire/auth';
import { PrintoclockService } from "./printoclock.service";
import { GimService } from "./gim.service";
import { OrderService } from "./order.service";
import { SettingsService } from "./settings.service";
import { CommerceService } from "./commerce.service";
import { Functions, httpsCallable } from '@angular/fire/functions';
import { OrganizationService } from "./organization.service";
import { UserService } from "./user.service";
import { ProductService } from "./product.service";
import { ShopService } from "./shop.service";
import { Organization } from "../models/organization.model";
import { Subject } from "rxjs";
import { Unsubscribe } from '@angular/fire/firestore';

@Injectable({
  providedIn: 'root',
})
/**
 * Auth service
 */
export class AuthService {
  private _currentUser: User;
  private organizationService = inject(OrganizationService);
  private userService = inject(UserService);
  private role: 'admin' | null = null;
  authChanged = new Subject<void>();
  /** Emits when the connected user's document changes (roles, production sites...) */
  currentUserChanged = new Subject<User>();
  private userWatch: Unsubscribe | null = null;

  public get currentUser(): User {
    return this._currentUser ?? new User();
  }

  public get isOrganizationAdmin(): boolean {
    return this.auth.currentUser &&
      this.organizationService.currentOrganization?.administrators.includes(this.auth.currentUser.uid);
  }

  public get isOrganizationUser(): boolean {
    return this.auth.currentUser && (
      this.organizationService.currentOrganization?.users.includes(this.auth.currentUser.uid) ||
      this.organizationService.currentOrganization?.administrators.includes(this.auth.currentUser.uid)
    );
  }

  public get isAnonymous(): boolean {
    return this.auth.currentUser?.isAnonymous ?? true;
  }

  public get isVerified(): boolean {
    return this.auth.currentUser?.emailVerified ?? true;
  }

  public get getUid(): string {
    return this.auth.currentUser?.uid ?? '';
  }

  /**
   * Check if a user is logged in
   * @returns True if the user is
   */
  public get isLoggedIn(): boolean {
    if (!this.auth) {
      return false;
    }
    return !!(this.auth.currentUser && !this.auth.currentUser.isAnonymous);
  }

  constructor(public auth: Auth,
              private router: Router,
              private functions: Functions,
              private injector: Injector) {
    this.initSubscribeToAuth();
  }

  public async waitAuth(): Promise<void> {
    while (!this._currentUser) {
      await new Promise(resolve => setTimeout(resolve, 100));
    }
  }

  /**
   * This listen to all users change, and not only to the connected user
   */
  private initSubscribeToAuth(): void {
    onIdTokenChanged(this.auth, authUser => {
      this.userWatch?.();
      this.userWatch = null;
      if (!authUser) {
        return signInAnonymously(this.auth);
      }
      if (authUser.isAnonymous) {
        this._currentUser = new User();
        this.role = null;
        this.authChanged.next();
        return;
      }
      /**
       * A user has been created and automatically logged in
       * We only need to detect sign in from signed out state and can ignore if currentUser is set
       */

      authUser.getIdTokenResult()
        .then(res => this.role = res.claims['role'] as 'admin' | null)
      this.userService.getUserByUid(authUser.uid).then(user => {
        if (this.auth.currentUser?.uid !== authUser.uid) {
          return;
        }
        this._currentUser = user ?? new User();
        this.authChanged.next();
        this.userWatch?.();
        this.userWatch = this.userService.watchUser(authUser.uid, updated => {
          if (this.auth.currentUser?.uid !== authUser.uid) {
            return;
          }
          this._currentUser = updated;
          this.currentUserChanged.next(updated);
        });
      }).catch(err => console.error(err));
    })
  }

  /**
   * Login with login/password
   * @param email The user email
   * @param password The user password
   */
  public async signIn(email: string, password: string): Promise<void> {
    try {
      await signInWithEmailAndPassword(this.auth, email, password);
    } catch (err) {
      console.error(err);
      const message = FirebaseErrors.Parse(err.code);
      throw new Error(message);
    }
  }

  /**
   * Create a new account
   * @param email email
   * @param password password
   * @param firstName firstName
   * @param lastName lastName
   * @param lang lang
   * @param uid The anonymous user id
   * @param redirect The redirect URI
   * @returns Promise
   */
  public async createAccount(email: string, password: string, firstName: string, lastName: string, lang: string,
    uid: string, redirect: string): Promise<void> {
    const res = await httpsCallable<{
      email: string;
      password: string;
      firstName: string;
      lastName: string;
      lang: string;
      shopId: string;
      uid: string;
      redirect: string;
    }, void>(this.functions, 'shop_sign_up_gen2')(
      { email, password, firstName, lastName, lang, shopId: 'okto', uid, redirect });
    return res.data;
  }

  /**
   * Transform an anonymous user to a real user
   * @param email The user email
   * @param password The user password
   * @returns UserCredential
   */
  public async linkAccount(email: string, password: string): Promise<UserCredential> {
    const credential = EmailAuthProvider.credential(email, password)
    if (this.auth.currentUser) {
      return await linkWithCredential(this.auth.currentUser, credential)
    }
    throw new Error();
  }

  /**
   * Email to confirm email address
   * @param email User email
   * @param lang User language
   * @returns promise
   */
  public async confirmationLink(email: string, lang: string): Promise<void> {
    return httpsCallable<{ email: string, shopId: string, lang: string }, void>
    (this.functions, 'shop_confirmation_link_gen2')({ email, shopId: 'okto', lang: lang })
      .then(res => res.data)
      .catch(err => {
        console.error(err);
        const message = FirebaseErrors.Parse(err.code);
        throw new Error(message);
      });
  }

  /**
   * Email to reset password
   * @param email User email
   * @param lang User language
   * @returns promise
   */
  public async resetPassword(email: string, lang: string): Promise<void> {
    return httpsCallable<{ email: string, shopId: string, lang: string }, void>
    (this.functions, 'shop_reset_password_gen2')({ email, shopId: 'okto', lang: lang })
      .then(res => res.data)
      .catch(err => {
        console.error(err);
        const message = FirebaseErrors.Parse(err.code);
        throw new Error(message);
      });
  }

  /**
   * Sign out the user
   * Remove it from the local storage
   * Redirect to the login page
   */
  public signOut(): void {
    this.userWatch?.();
    this.userWatch = null;
    this.unsubscribe();
    signOut(this.auth).then(() => {
      return this.router.navigate(['auth', 'login'])
    }).catch(err => {
      console.error(err);
      throw err;
    });
  }

  /**
   * Check if the current user has at least one of the role
   * @param role The role or array of roles
   * @param user The user to check
   * @param organization The organization to check in, if undefined, the current organization is used
   * @returns true if the user has one of the role, false otherwise
   */
  public hasRole(
    role: RolesEnum | RolesEnum[],
    user = this._currentUser, organization: Organization | undefined = undefined
  ): boolean {
    const org = organization ?? this.organizationService.currentOrganization;
    if (org?.administrators.includes(user.id)) {
      return true;
    }
    const roles = new Set(Array.isArray(role) ? role : [role]);
    const currentOrgRoles = user?.roles[org?.id] ?? [];
    return currentOrgRoles.some(r => roles.has(r));
  }

  /**
   * Unsubscribe from all data
   */
  private unsubscribe(): void {
    this.injector.get(PrintoclockService).unsubscribe();
    this.injector.get(GimService).unsubscribe();
    this.injector.get(OrderService).unsubscribe();
    this.injector.get(SettingsService).unsubscribe();
    this.injector.get(CommerceService).unsubscribe();
    this.injector.get(ProductService).unsubscribe();
    this.injector.get(ShopService).unsubscribe();
  }

  /**
   * Refresh connected user
   * @returns void
   */
  refreshUser(): Promise<void> {
    return this.auth?.currentUser?.reload();
  }

  isSuperAdmin(): boolean {
    return this.role === 'admin';
  }
}
